// client/src/services/import.ts
// API calls for the Data Import feature.

// ── Test result shapes ─────────────────────────────────────────────────────
// Different use cases return different shapes; keep this loose and let the
// component narrow by useCaseId.

export interface RollJurisdictionsTestResult {
  jobIdx: string;
  jurisdictionCount: number;
  originalJurisdictions: Record<string, unknown>[];
  modifiedJurisdictions: Record<string, unknown>[];
  savePayload: Record<string, unknown>;
  peResponse?: string;
  _debug?: Record<string, unknown>;
}

export interface SetClientAttributesTestResult {
  clientCode: string;
  entityId: number;
  clientName?: string;
  submitItem: Record<string, unknown>;
  peResponse?: string;
}

export interface WriteoffDisbursementsTestResult {
  clientCode: string;
  clientName?: string;
  contIndex: number;
  wipIndex: number;
  servPeriod: number;
  wipOutstanding: number;
  draftIdx: number | null;
  pulledWipsTotal?: number;
  targetWipsTotal?: number;
  stage?: string;
}

export interface StaffBudgetResolvedRow {
  BudgetIndex: number;
  PeriodIndex: number;
  StaffIndex: number;
  PracID: number;
  ProdHrs: number;
  StdHrs: number;
  [key: string]: unknown;
}

export interface UpdateStaffBudgetTestResult {
  employeeName: string;
  budgetYear: number;
  staffIndex: number;
  monthsProvided: string[];
  monthsSkipped: string[];
  monthsUnmatched: string[];
  periodsResolved: number;
  resolvedRows: StaffBudgetResolvedRow[];
  peResponse?: string;
}

export interface CreateJobsTestResult {
  clientCode: string;
  clientName?: string;
  contIndex: number;
  jobName: string;
  jobCode: string;
  partner?: number;
  partnerName?: string | null;
  manager?: number;
  managerName?: string | null;
  addService?: boolean;
  serviceActive?: boolean;
  status: 'success' | 'skipped';
  reason?: string;
  payload?: Record<string, unknown>;
  peResponse?: string;
}

export interface UpdateClientRecordTestResult {
  clientCode: string;
  contIndex: number;
  clientName?: string;
  updates: Record<string, string>;
  before: Record<string, unknown>;
  status: 'success';
  peResponse?: string;
}

export interface CreateClientTestResult {
  clientCode: string;
  clientName: string;
  contIndex: number | null;
  requestId?: number;
  isIndividual?: boolean;
  entity?: string;
  partner?: string;
  manager?: string;
  office?: string;
  clientType?: string;
  status: 'success' | 'skipped';
  reason?: string;
}

export interface ModifyJobsTestResult {
  jobIdx: number;
  clientCode: string;
  jobName: string;
  updates: Record<string, string>;
  before: Record<string, unknown>;
  status: 'success';
  peResponse?: string;
}

export interface JobBudgetTaskResult {
  row: number;
  taskSubject: string;
  taskCode?: string;
  taskIndex?: number;
  hoursBefore?: number;
  hoursAfter?: number;
  status: 'success' | 'error' | 'skipped';
  error?: string;
  reason?: string;
}

export interface UpdateClientStatusTestResult {
  contIndex: number;
  clientCode?: string;
  clientName?: string;
  statusBefore?: string;
  statusAfter?: string;
  status: 'success' | 'skipped';
  reason?: string;
  peResponse?: string;
}

export interface UpdateContactStatusTestResult {
  contIndex: number;
  contName?: string;
  loadedVia?: 'client' | 'contact';
  statusBefore?: string;
  statusBeforeName?: string;
  statusAfter?: string;
  statusAfterName?: string;
  status: 'success' | 'skipped';
  reason?: string;
  peResponse?: string;
}

export interface UpdateJobBudgetTestResult {
  jobIdx: number;
  taskCount: number;
  budgetRowCount?: number;
  jobBudgetHoursBefore: number;
  jobBudgetHoursAfter: number;
  tasksApplied: number;
  taskResults: JobBudgetTaskResult[];
  savePayload?: Record<string, unknown>;
  status: 'success' | 'skipped';
  reason?: string;
  peResponse?: string;
  // Post-save read-back (test mode only) — proves what PE actually stored
  verification?: {
    budgetRowCount?: number;
    expectedRowCount?: number;
    jobBudgetHours?: number;
    tasks?: { taskSubject: string; taskIndex: number; hours: number; etc: number }[];
    error?: string;
  };
}

export interface UpdateJobComplexityTestResult {
  jobIdx: number;
  complexityBefore: string;
  complexityBeforeName?: string;
  complexityAfter: string;
  complexityName?: string;
  source?: 'row' | 'form';
  savePayload?: Record<string, unknown>;
  status: 'success' | 'skipped';
  reason?: string;
  peResponse?: string;
}

export interface UpdateContactRecordTestResult {
  contIndex: number;
  contName?: string;
  loadedVia?: 'client' | 'contact';
  valueSource?: 'json' | 'fields';
  ignoredKeys?: string[];
  alreadyMatching?: string[];
  before?: Record<string, unknown>;
  updates?: Record<string, string>;
  updatedFields?: string;
  status: 'success' | 'skipped';
  reason?: string;
  peResponse?: string;
}

/** How a supplied staff value was interpreted by peStaffResolver. */
export type StaffMatchedOn = 'index' | 'email' | 'name' | 'name-reordered' | 'user';

export interface JobManagementRoleChange {
  role: 'JP' | 'JM';
  roleLabel: string;
  field: 'Job_Partner' | 'Job_Manager';
  before: number | null;
  beforeName?: string;
  after: number;
  afterName?: string;
  afterEmail?: string;
  matchedOn?: StaffMatchedOn;
  inactive?: boolean;
  source: 'row' | 'form';
}

export interface UpdateJobManagementTestResult {
  jobIdx: number;
  mode: 'single' | 'both';
  rolesRequested?: string;
  rolesUpdated?: string;
  partnerBefore?: number | null;
  partnerBeforeName?: string;
  partnerAfter?: number | null;
  partnerAfterName?: string;
  managerBefore?: number | null;
  managerBeforeName?: string;
  managerAfter?: number | null;
  managerAfterName?: string;
  matchedOn?: string;
  warnings?: string[];
  changes?: JobManagementRoleChange[];
  unchanged?: JobManagementRoleChange[];
  savePayload?: Record<string, unknown>;
  status: 'success' | 'skipped';
  reason?: string;
  peResponse?: string;
}

export interface UpdateJobRolesTestResult {
  jobIdx: number;
  roleIndex: number;
  roleName?: string;
  mode: 'replace' | 'add';
  action?: 'replace' | 'add' | 'unassign';
  staffBefore?: string;
  staffAfter?: string;
  primaryBefore?: number;
  primaryAfter?: number;
  matchedOn?: string;
  source?: 'row' | 'form';
  warnings?: string[];
  savePayload?: Record<string, unknown>;
  status: 'success' | 'skipped';
  reason?: string;
}

export type TestResult =
  | RollJurisdictionsTestResult
  | SetClientAttributesTestResult
  | WriteoffDisbursementsTestResult
  | UpdateStaffBudgetTestResult
  | CreateJobsTestResult
  | UpdateClientRecordTestResult
  | ModifyJobsTestResult
  | UpdateClientStatusTestResult
  | UpdateContactStatusTestResult
  | UpdateContactRecordTestResult
  | UpdateJobBudgetTestResult
  | UpdateJobComplexityTestResult
  | UpdateJobManagementTestResult
  | UpdateJobRolesTestResult
  | CreateClientTestResult
  | Record<string, unknown>;

export interface ExecuteRowResult {
  row: number;
  status: 'success' | 'error' | 'skipped';
  error?: string;
  reason?: string;
  // roll-jurisdictions
  jobIdx?: string;
  jurisdictionCount?: number;
  // set-client-attributes
  clientCode?: string;
  clientName?: string;
  entityId?: number;
  fieldId?: string;
  submittedValue?: unknown;
  // writeoff-disbursements
  contIndex?: number;
  wipIndex?: number;
  servPeriod?: number;
  wipOutstanding?: number;
  draftIdx?: number | null;
  stage?: string;
  // update-staff-budget
  employeeName?: string;
  budgetYear?: number;
  periodsUpdated?: number;
  monthsSkipped?: string[];
  // create-jobs
  jobName?: string;
  jobCode?: string;
  partnerName?: string | null;
  managerName?: string | null;
  addService?: boolean;
  serviceActive?: boolean;
  // update-client-record
  updates?: Record<string, string>;
  before?: Record<string, unknown>;
  updatedFields?: string;
  // update-client-status / update-contact-status
  statusBefore?: string;
  statusAfter?: string;
  statusBeforeName?: string;
  statusAfterName?: string;
  contName?: string;
  loadedVia?: 'client' | 'contact';
  // update-job-budget
  taskSubject?: string;
  taskCode?: string;
  taskIndex?: number;
  hoursBefore?: number;
  hoursAfter?: number;
  jobBudgetHours?: number;
  // update-job-complexity
  complexityBefore?: string;
  complexityBeforeName?: string;
  complexityAfter?: string;
  complexityName?: string;
  // update-contact-record
  valueSource?: 'json' | 'fields';
  ignoredKeys?: string[];
  alreadyMatching?: string[];
  // update-job-management
  mode?: 'single' | 'both';
  rolesRequested?: string;
  rolesUpdated?: string;
  partnerBefore?: number | null;
  partnerBeforeName?: string;
  partnerAfter?: number | null;
  partnerAfterName?: string;
  managerBefore?: number | null;
  managerBeforeName?: string;
  managerAfter?: number | null;
  managerAfterName?: string;
  matchedOn?: string;
  warnings?: string[];
  // update-service-roles
  servIndex?: string;
  servTitle?: string;
  serviceStaffBefore?: number | null;
  serviceStaffAfter?: number | null;
  serviceStaffAfterName?: string;
  verified?: boolean;
  // update-job-roles
  roleIndex?: number;
  roleName?: string;
  action?: 'replace' | 'add' | 'unassign';
  staffBefore?: string;
  staffAfter?: string;
  // shared
  peResponse?: string;
}

export interface ExecuteResult {
  total: number;
  succeeded: number;
  failed: number;
  skipped?: number;
  results: ExecuteRowResult[];
}

// ── Form-level params (attached to test/execute requests) ───────────────────

export interface SetClientAttributesParams {
  AttrIndex: number;
  FieldId: string;
  value: string | null;
  valueColumn: string | null; // null = constant mode; non-null = per-row mode
}

export interface WriteoffDisbursementsParams {
  debtTranDate: string; // ISO date "YYYY-MM-DD"
  narrative?: string;
}

export type ImportParams =
  | SetClientAttributesParams
  | WriteoffDisbursementsParams
  | Record<string, unknown>
  | undefined;

// ── Calls ──────────────────────────────────────────────────────────────────

export async function testImportRecord(
  useCase: string,
  record: Record<string, string>,
  params?: ImportParams,
): Promise<TestResult> {
  const res = await fetch('/api/import-test', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ useCase, record, params }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }

  return res.json();
}

export interface QueryResult {
  headers: string[];
  rows: Record<string, string>[];
  totalCount: number;
}

export async function runImportQuery(sql: string): Promise<QueryResult> {
  const res = await fetch('/api/import-query', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sql }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }

  return res.json();
}

export async function executeImport(
  useCase: string,
  records: Record<string, string>[],
  params?: ImportParams,
): Promise<ExecuteResult> {
  const res = await fetch('/api/import-execute', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ useCase, records, params }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }

  return res.json();
}

// ── Attribute picker (set-client-attributes use case) ──────────────────────

export interface AttributeListItem {
  AttrIndex: number;
  AttrName: string;
  AttrType: string;
}

export async function searchAttributes(q: string, take = 100): Promise<AttributeListItem[]> {
  const params = new URLSearchParams({ action: 'search', q, take: String(take) });
  const res = await fetch(`/api/import-attribute-meta?${params}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  const data = await res.json();
  return data.results || [];
}

export interface AttributeFieldOption {
  fieldId: 'AttrText' | 'AttrBool' | 'AttrNumber' | 'AttrDate' | 'AttrCur' | 'AttrValid';
  prompt: string;
  order: number;
  validSource?: string | null;
}

export interface AttributeDetail {
  attrIndex: number;
  attrName: string;
  attrType: string;
  supported: AttributeFieldOption[];
}

export async function getAttributeDetail(attrIndex: number): Promise<AttributeDetail> {
  const params = new URLSearchParams({ action: 'detail', attrIndex: String(attrIndex) });
  const res = await fetch(`/api/import-attribute-meta?${params}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

// ── Job templates (create-jobs use case) ───────────────────────────────────

export interface JobService { ServIndex: string; ServTitle: string; }

export interface JobTemplate {
  JobTmp_Idx: number;
  ServIndex: string;
  ServTitle: string;
  JobName: string;   // pattern with [YYYY]/[TTTT] year placeholders
  JobCode: string;   // pattern
  Partner: number;
  PartnerName: string;
  Manager: number;
  ManagerName: string;
  InCharge: number;
  InChargeName: string;
  Value: number;
}

export interface JobStaff { StaffIndex: number; StaffName: string; }

export interface JobMeta {
  clientCode: string;
  contIndex: number;
  clientName?: string;
  services: JobService[];
  templates: JobTemplate[];
  staff: JobStaff[];
}

export async function getJobMeta(clientCode: string): Promise<JobMeta> {
  const params = new URLSearchParams({ clientCode });
  const res = await fetch(`/api/import-job-meta?${params}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

// ── Job role types (update-job-roles use case) ─────────────────────────────

export interface JobRoleType { RoleIndex: number; RoleName: string; }

/** Firm-wide role types. PE only serves them per job, so pass any real Job_Idx. */
export async function getRoleMeta(jobIdx: number | string): Promise<JobRoleType[]> {
  const params = new URLSearchParams({ jobIdx: String(jobIdx) });
  const res = await fetch(`/api/import-role-meta?${params}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  const data = await res.json();
  return data.roleTypes || [];
}
